import { ExpensesEntitiesExpensableResourceTypeEnum } from 'generated/resources/graphql'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import {
  CustomizationField,
  fieldEnabled as isEnabled,
  isMandatory,
} from 'modules/expenses/hooks/useExpensesCustomization'
import { ProjectWorker } from 'modules/expenses/hooks/useProjects'

const defaultRequiredMessage = () => ({
  message: i18n.t('expenses_v2.forms.errors.required'),
})

export interface FormContext {
  customizationFields: CustomizationField[]
  hasProjectsFeature: boolean
  projectWorkers: ProjectWorker[]
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
}

export const ProjectsSchema = ({
  customizationFields,
  hasProjectsFeature,
  projectWorkers,
  resourceType,
}: FormContext) => {
  return z.object({
    projects: z
      .array(
        z.object({
          projectWorker: z.object({ id: z.string().nullish() }).nullish(),
          subproject: z.object({ id: z.string().nullish() }).nullish(),
        })
      )
      .optional()
      .refine((projects) => {
        if (
          !shouldShowBlock(hasProjectsFeature, projectWorkers, customizationFields, resourceType)
        ) {
          return true
        }

        if (!isMandatory('projects', resourceType, customizationFields)) {
          return true
        }

        const selectedProjectId = projects?.[0]?.projectWorker?.id
        if (!selectedProjectId) {
          return false
        }

        if (!hasSubprojects(projectWorkers, String(selectedProjectId))) {
          return true
        }

        const selectedSubprojectId = projects?.[0]?.subproject?.id
        return !!selectedSubprojectId
      }, defaultRequiredMessage()),
  })
}

export const shouldShowBlock = (
  hasProjectsFeature: boolean,
  projectWorkers: ProjectWorker[],
  customizationFields: CustomizationField[],
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
) =>
  hasProjectsFeature &&
  projectWorkers.length > 0 &&
  isEnabled(customizationFields, resourceType, 'projects')

const hasSubprojects = (projectWorkers: ProjectWorker[], selectedProjectWorkerId: string) => {
  const selectedProjectWorker = projectWorkers.find(
    (projectWorker) => projectWorker.id === selectedProjectWorkerId
  )
  const subjectProjectsCount = selectedProjectWorker?.imputableProject?.subprojects?.length
  return subjectProjectsCount && subjectProjectsCount > 0
}

export type ProjectsSchema = z.infer<ReturnType<typeof ProjectsSchema>>
